function findByText(text, exact = false) {
	const els = document.querySelectorAll('span, p, label, h6, h5, h4, button, .MuiTypography-root')
	for (const el of els) {
		const content = el.textContent.trim().toLowerCase()
		if (exact ? content === text.toLowerCase() : content.includes(text.toLowerCase())) {
			return el
		}
	}
	return null
}

function findByLabel(text) {
	const labels = document.querySelectorAll('label, .MuiFormLabel-root, .MuiInputLabel-root')
	for (const label of labels) {
		if (label.textContent.toLowerCase().includes(text.toLowerCase())) {
			return label.closest('.MuiFormControl-root') || label.closest('.MuiGrid-item') || label.parentElement
		}
	}
	return null
}

const ES = navigator.language?.startsWith('es')

export const TOUR_STEPS = [
	{
		type: 'intro',
		icon: 'chart-mixed',
		title: ES ? 'Bienvenido a Business Intelligence' : 'Welcome to Business Intelligence',
		description: ES
			? 'La página de BI es tu herramienta avanzada de comparación de precios. Configura filtros detallados, compara precios entre proveedores, oficinas y periodos de tiempo, y visualiza los resultados en tablas y gráficos. Guarda y comparte tus configuraciones de filtros mediante URL.'
			: 'The BI page is your advanced price comparison tool. Configure detailed filters, compare prices across providers, offices, and time periods, then visualize results in tables and charts. Save and share your filter configurations via URL.',
	},
	{
		type: 'field',
		findTarget: () => {
			const icon = document.querySelector('.fa-floppy-disk') || document.querySelector('[data-icon="floppy-disk"]')
			if (icon) {
				const btn = icon.closest('button')
				if (btn?.parentElement) return btn.parentElement
			}
			return null
		},
		icon: 'link',
		title: ES ? 'Compartir y Guardar Filtros' : 'Share & Save Filters',
		description: ES
			? 'Tres acciones para gestionar tus configuraciones de filtros. Copia un enlace compartible con todos los filtros actuales codificados en la URL, guarda la configuración con un nombre personalizado para más tarde, o carga un conjunto de filtros guardado previamente desde el desplegable.'
			: 'Three actions to manage your filter configurations. Copy a shareable link with all current filters encoded in the URL, save the configuration with a custom name for later, or load a previously saved filter set from the dropdown.',
		example: ES
			? 'Copiar Enlace | Guardar Filtros | Cargar Filtros Guardados'
			: 'Copy Link | Save Filters | Load Saved Filters',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Total price') || findByText('Daily', true) || findByText('Office Group') || findByText('Site Group')
			if (el) {
				const grid = el.closest('.MuiGrid-container')
				if (grid) return grid
			}
			return null
		},
		icon: 'toggle-on',
		title: ES ? 'Selectores de Filtro' : 'Filter Toggles',
		description: ES
			? 'Selectores rápidos para cambiar modos de filtrado: activa la normalización de Precio Diario, alterna entre Oficinas individuales y Grupos de Oficinas, Sitios individuales y Grupos de Sitios, y habilita la comparación con un Periodo Anterior.'
			: 'Quick toggles to change filter modes: enable Daily Price normalization, switch between individual Offices vs Office Groups, individual Sites vs Site Groups, and enable comparison with a Previous Period.',
		example: ES
			? 'Precio Diario | Grupo de Oficinas | Grupo de Sitios | Comparación Anterior'
			: 'Daily Price | Office Group | Site Group | Previous Comparison',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByLabel('Grouping method') || findByLabel('How to group')
			if (el) return el.closest('.MuiGrid-item') || el
			return null
		},
		icon: 'layer-group',
		title: ES ? 'Método de Agrupación' : 'Grouping Method',
		description: ES
			? 'Define cómo se organizan los resultados en la tabla. Elige cómo cruzar tus datos: por proveedor y fecha de recogida, categoría y recogida, LOR y recogida, fecha de reserva, o proveedor y oficina. Esto determina la estructura de la tabla.'
			: 'Defines how results are organized in the table. Choose how to cross-reference your data: by provider and pickup date, category and pickup, LOR and pickup, booking date, or provider and office. This determines the table structure.',
		example: ES
			? 'Proveedor-Recogida | Categoría-Recogida | LOR-Recogida | Oficina-Reserva...'
			: 'Provider-Pickup | Category-Pickup | LOR-Pickup | Office-Booking...',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByLabel('Providers') || findByLabel('Categories')
			if (el) {
				let parent = el.closest('.MuiGrid-container')
				if (parent?.parentElement) {
					const box = parent.parentElement
					const grids = box.querySelectorAll('.MuiGrid-container')
					if (grids.length >= 2) return box
				}
				return parent
			}
			return null
		},
		icon: 'filter',
		title: ES ? 'Filtros de Búsqueda' : 'Search Filters',
		description: ES
			? 'Configura tu comparación de precios: selecciona oficinas, sitios, rangos de fechas de reserva y recogida, agrupación, categorías, LOR, niveles, proveedores y moneda. Despliega "Mostrar Características" para filtros avanzados como ACRISS, kilometraje, transmisión, política de combustible y seguros.'
			: 'Configure your price comparison: select offices, sites, booking and pickup date ranges, grouping, categories, LOR, tiers, providers, and currency. Expand "Show Features" for advanced filters like ACRISS, mileage, transmission, fuel policy, and insurance.',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Min. Price') || findByText('Avg. Price')
			if (el) {
				const toggle = el.closest('.MuiToggleButtonGroup-root') || el.closest('[role="group"]')
				if (toggle) {
					const container = toggle.parentElement
					if (container?.children?.length >= 1) return container
				}
				return toggle
			}
			return null
		},
		skipIf: () => !findByText('Min. Price'),
		icon: 'sliders',
		title: ES ? 'Acciones de la Tabla' : 'Table Actions',
		description: ES
			? 'Controles encima de la tabla de resultados. Alterna entre precios Mínimo, Medio y Máximo. Cuando se devuelven varias oficinas, aparece un filtro de oficina para centrarse en una ubicación específica, además de un botón para descargar todas las oficinas como Excel.'
			: 'Controls above the results table. Toggle between Min, Average, and Max prices. When multiple offices are returned, an office filter appears to focus on one specific location, plus a button to download all offices as Excel.',
		example: ES
			? 'Precio Mín./Medio/Máx. | Filtrar por Oficina | Descargar Todas las Oficinas Excel'
			: 'Min/Avg/Max Price | Filter by Office | Download All Offices Excel',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Price Comparison')
			if (el) {
				const paper = el.closest('.MuiPaper-root')
				if (paper) return paper
			}
			const tables = document.querySelectorAll('table')
			for (const t of tables) {
				if (t.querySelector('th') && !t.textContent.includes('no matching records')) {
					return t.closest('.MuiPaper-root') || t
				}
			}
			return null
		},
		skipIf: () => !findByText('Price Comparison') && !document.querySelector('table th'),
		icon: 'table-cells',
		title: ES ? 'Tabla de Comparación de Precios' : 'Price Comparison Table',
		description: ES
			? 'Tabla de resultados con la primera columna fija a la izquierda (categoría, proveedor u oficina según la agrupación). Las columnas de fecha muestran precios con bordes codificados por color: verde para el más barato, rojo para el más caro. Cada celda incluye el precio actual, el precio anterior y el porcentaje de evolución con flechas direccionales.'
			: 'Results table with the first column sticky on the left (category, provider, or office depending on grouping). Date columns show prices with color-coded borders: green for cheapest, red for most expensive. Each cell includes the current price, previous price, and evolution percentage with directional arrows.',
	},
	{
		type: 'field',
		findTarget: () => {
			const el = findByText('Chart')
			if (el) {
				const paper = el.closest('.MuiPaper-root')
				if (paper) return paper
			}
			const chart = document.querySelector('[id*="Chart"]') || document.querySelector('[id*="chart"]')
			return chart?.closest('.MuiPaper-root') || chart?.parentElement
		},
		skipIf: () => !findByText('Chart') && !document.querySelector('[id*="chart"]'),
		icon: 'chart-line',
		title: ES ? 'Gráfico de Tendencia de Precios' : 'Price Trend Chart',
		description: ES
			? 'Gráfico de líneas que visualiza las tendencias de precios a lo largo del tiempo. Cada serie representa un proveedor o categoría. Cuando la Comparación con Periodo Anterior está activada, las líneas discontinuas muestran el periodo previo. Haz clic en los elementos de la leyenda para mostrar/ocultar series individuales. El selector Mín./Media/Máx. también se aplica aquí.'
			: 'Line chart visualizing price trends over time. Each series represents a provider or category. When Previous Comparison is enabled, dashed lines show the prior period. Click legend items to show/hide individual series. Toggle Min/Avg/Max applies here too.',
	},
	{
		type: 'outro',
		icon: 'star',
		title: ES ? '¡Todo Listo!' : "You're All Set!",
		description: ES
			? 'Configura tus filtros y pulsa Buscar. Usa el selector Mín./Media/Máx. para cambiar la vista de precios. Comparte tu análisis con el botón de copiar enlace, o guárdalo para más tarde. La tabla y el gráfico se actualizan juntos — explora tendencias y detecta ventajas competitivas.'
			: 'Configure your filters and hit Search. Use the Min/Avg/Max toggle to switch price views. Share your analysis via the copy link button, or save it for later. The table and chart update together — explore trends and spot competitive advantages.',
	},
]
